import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import type { SessionUser } from '@workforce/shared';
import { api } from '@/lib/api';
import { Button, ErrorBanner, Field, Input } from '@/components/ui';
import { ThemeToggle } from '@/lib/theme';
import { Logo } from '@/components/Logo';
import { LanguageSwitch, t } from '@/lib/i18n';

export function LoginPage() {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await api.post<{ user: SessionUser }>('/auth/login', { email, password });
      qc.setQueryData(['auth', 'me'], res.user);
    } catch (err) {
      setError(err);
      setPassword('');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-surface to-brand-50 px-4">
      <div className="absolute end-4 top-4 flex items-center gap-2">
        <LanguageSwitch />
        <ThemeToggle />
      </div>
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="mx-auto inline-block rounded-2xl bg-white px-5 py-4 shadow-sm ring-1 ring-slate-200/60">
            <Logo variant="full" alt={t('Company logo')} className="mx-auto h-24 w-auto max-w-[16rem]" />
          </div>
          <h1 className="mt-6 text-2xl font-semibold tracking-tight">{t('Sign in to Workforce')}</h1>
          <p className="mt-1 text-sm text-slate-500">{t('Leave, attendance, payroll and time tracking')}</p>
        </div>
        <form onSubmit={submit} className="card space-y-4 p-6" noValidate>
          <ErrorBanner error={error} />
          <Field label={t('E-mail')}>
            <Input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
          </Field>
          <Field label={t('Password')}>
            <Input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <Button type="submit" className="w-full" loading={loading} icon={<Lock className="h-4 w-4" />} disabled={!email || !password}>
            {t('Sign in')}
          </Button>
        </form>
        <p className="mt-6 text-center text-xs text-slate-400">{t('Forgot your password? Ask your administrator to reset it.')}</p>
      </div>
    </div>
  );
}
